// Relevé d'un îlot sur le terrain (D-303) : l'agent se place à chacun des quatre coins (position
// moyennée sur 30 s), compte les rangées et les places par rangée, mesure la façade au mètre ou au
// pas. L'opération releve_ilot part par la file de synchronisation ; le serveur recale et redécoupe.
import { LONGUEUR_PAS_DEFAUT_M, schemaChargeReleveIlot } from '@cc/contrats';
import { Bandeau, Bouton, Champ } from '@cc/ui';
import { useRef, useState } from 'react';
import { messageErreur } from '../../api/erreurs';
import { uuidV7 } from '../../terrain/identifiants';
import type { IlotEmporte } from '../../terrain/ilots';
import { facadeMetres, releverCoin, type CoinReleve } from '../../terrain/releve-coin';
import { synchroniseurPartage } from '../../terrain/synchronisation';
import { Section } from './commun';
import styles from './terrain.module.css';

const NOMS_COINS = [
  'A (début de la façade)',
  'B (fin de la façade)',
  'C (fond, côté B)',
  'D (fond, côté A)',
];

export function ReleveIlot({ emporte, attente }: { emporte: IlotEmporte; attente: boolean }) {
  const { ilot } = emporte;
  const [coins, setCoins] = useState<(CoinReleve | null)[]>([null, null, null, null]);
  const [enCours, setEnCours] = useState<number | null>(null);
  const [progres, setProgres] = useState(0);
  const [rangees, setRangees] = useState(ilot.rangees);
  const [parRangee, setParRangee] = useState(ilot.places_par_rangee);
  const [methode, setMethode] = useState<'metre' | 'pas'>('metre');
  const [valeur, setValeur] = useState<number | ''>('');
  const [pas, setPas] = useState(LONGUEUR_PAS_DEFAUT_M);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const arret = useRef<(() => void) | null>(null);

  const relever = async (i: number) => {
    setErreur(null);
    setEnCours(i);
    setProgres(0);
    const r = releverCoin(undefined, (n) => setProgres(n));
    arret.current = r.arreter;
    try {
      const coin = await r.promesse;
      setCoins((c) => c.map((x, k) => (k === i ? coin : x)));
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(null);
      arret.current = null;
    }
  };
  const envoyer = async () => {
    setErreur(null);
    try {
      const charge = schemaChargeReleveIlot.parse({
        coins,
        rangees,
        places_par_rangee: parRangee,
        ...(valeur
          ? {
              facade: {
                methode,
                valeur: Number(valeur),
                ...(methode === 'pas' ? { longueur_pas_m: pas } : {}),
              },
            }
          : {}),
      });
      await synchroniseurPartage().enfiler(
        {
          id_client: uuidV7(),
          type: 'releve_ilot',
          objet_type: 'ilot_marche',
          objet_id: ilot.id,
          version_attendue: ilot.version,
          enregistre_le: new Date().toISOString(),
          charge,
        },
        { titre: 'Relevé d’îlot', objet_libelle: `Îlot ${ilot.code}` },
      );
      setCoins([null, null, null, null]);
      setMessage(
        'Relevé enregistré sur cet appareil. L’îlot sera recalé à la synchronisation ; téléchargez-le ensuite pour recenser sur le nouveau découpage.',
      );
    } catch (e) {
      setErreur(messageErreur(e));
    }
  };
  const complet = coins.every(Boolean);
  return (
    <Section titre="Relevé de l’îlot">
      <div className={styles.pile}>
        <p className={styles.aide}>
          Placez-vous à chaque coin et restez immobile 30 secondes. La façade de la rangée 1 va du
          coin A au coin B. Mesurez la façade au mètre si possible : elle corrige l’erreur du GPS.
        </p>
        {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
        {message ? <Bandeau type="info">{message}</Bandeau> : null}
        <ul className={styles.liste} aria-label="Coins de l’îlot">
          {NOMS_COINS.map((nom, i) => (
            <li key={nom} className={styles.ligneSansChevron}>
              <span className={styles.ligneTexte}>
                <span className={styles.ligneTitre}>Coin {nom}</span>
                <span className={styles.ligneSous}>
                  {enCours === i
                    ? `Relevé en cours : ${progres} position${progres > 1 ? 's' : ''} reçue${progres > 1 ? 's' : ''}`
                    : coins[i]
                      ? `${coins[i]!.echantillons} positions moyennées, dispersion ${coins[i]!.ecart_m.toLocaleString('fr-FR')} m`
                      : 'Non relevé'}
                </span>
              </span>
              {enCours === i ? (
                <Bouton petit variante="secondaire" onClick={() => arret.current?.()}>
                  Terminer maintenant
                </Bouton>
              ) : (
                <Bouton
                  petit
                  variante={coins[i] ? 'discret' : 'secondaire'}
                  disabled={enCours !== null || attente}
                  onClick={() => void relever(i)}
                >
                  {coins[i] ? 'Refaire' : 'Relever (30 s)'}
                </Bouton>
              )}
            </li>
          ))}
        </ul>
        <div className={styles.ligneBoutons}>
          <Champ
            libelle="Rangées comptées"
            type="number"
            min={1}
            max={200}
            value={rangees}
            onChange={(e) => setRangees(Number(e.target.value))}
            required
          />
          <Champ
            libelle="Places par rangée"
            type="number"
            min={1}
            max={500}
            value={parRangee}
            onChange={(e) => setParRangee(Number(e.target.value))}
            required
          />
        </div>
        <fieldset className={styles.pile}>
          <legend>Mesure de la façade (facultative)</legend>
          <div className={styles.choix} role="radiogroup" aria-label="Méthode de mesure">
            {(['metre', 'pas'] as const).map((m) => (
              <label key={m}>
                <input
                  type="radio"
                  name="methode-facade"
                  value={m}
                  checked={methode === m}
                  onChange={() => setMethode(m)}
                />
                {m === 'metre' ? 'Au mètre' : 'Au pas'}
              </label>
            ))}
          </div>
          <div className={styles.ligneBoutons}>
            <Champ
              libelle={methode === 'metre' ? 'Façade (m)' : 'Nombre de pas'}
              type="number"
              min={0}
              step={methode === 'metre' ? 0.1 : 1}
              value={valeur}
              onChange={(e) => setValeur(e.target.value === '' ? '' : Number(e.target.value))}
            />
            {methode === 'pas' ? (
              <Champ
                libelle="Longueur d’un pas (m)"
                type="number"
                min={0.3}
                max={1.2}
                step={0.05}
                value={pas}
                onChange={(e) => setPas(Number(e.target.value))}
              />
            ) : null}
          </div>
          {valeur && methode === 'pas' ? (
            <p className={styles.aide}>
              Façade estimée : {facadeMetres('pas', Number(valeur), pas).toLocaleString('fr-FR')} m.
            </p>
          ) : null}
        </fieldset>
        <Bouton disabled={!complet || attente || enCours !== null} onClick={() => void envoyer()}>
          Enregistrer le relevé
        </Bouton>
      </div>
    </Section>
  );
}
